/**
 * Slugs, for stable keys that survive a rename.
 *
 * ⚠️ THE ONE THING THIS IS FOR: a command role's `key` (see `policy.schema.ts`).
 *
 * A key is minted ONCE from the label the operator first typed, and is never
 * recomputed. `label` stays freely editable; `key` never changes. Without that,
 * "In Charge" in one policy and "Incharge" in another are different things for ever, and
 * any cross-policy question about who commanded what becomes guesswork.
 *
 * It lives in `packages/shared` rather than in the API so the policy builder can show the
 * operator the key their label will get, from the same function that will mint it. Two
 * implementations of a slug is two slugs.
 */

/** What a minted key looks like: lowercase alphanumeric runs joined by single `_`. */
export const SLUG_REGEX = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

export const SLUG_MAX_LENGTH = 40;

/**
 * `'Officer in Charge'` → `'officer_in_charge'`, `'Asst. Sub-Inspector'` → `'asst_sub_inspector'`.
 *
 * Returns `''` for input with no alphanumeric content at all — the caller decides what
 * that means (`policy.schema.ts` makes it a 422, because a role whose key would be empty
 * is a role that cannot be referred to).
 *
 * NFD-normalises and strips combining marks first, so `'Inchargé'` and `'Incharge'` do
 * not become two different keys for what an operator typed as one word.
 */
export const toSlug = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    // The slice can leave a trailing separator behind; trim again rather than emit a key
    // that its own regex would reject.
    .replace(/_+$/g, '');
